import Link from "next/link";
import { AudienceSection } from "@/components/blocks/AudienceSection";
import { cardBodyClass, cardHeadingClass, cardSurfaceClass } from "@/lib/cardSurface";
import { cn } from "@/lib/utils";

type ShowcaseItem = {
  kicker?: string;
  title?: string;
  body?: string;
  mobileTitle?: string;
  mobileBody?: string;
  wrap?: "wrap" | "truncate" | "stack";
};

function wrapClass(wrap: ShowcaseItem["wrap"]): string {
  if (wrap === "truncate") return "max-md:truncate";
  if (wrap === "stack") return "max-md:flex-col max-md:items-stretch";
  return "max-md:whitespace-normal max-md:break-words";
}

function proofCardClass(content: Record<string, unknown>, extra = "") {
  return cn(
    "rounded-[var(--border-radius)] border border-[var(--color-border)] bg-[var(--color-surface)] p-5",
    cardSurfaceClass(content.cardSurface),
    extra,
  );
}

function ItemCopy({ item, tone = "theme" }: { item: ShowcaseItem; tone?: "theme" | "terminal" }) {
  const title = String(item.title ?? "");
  const body = String(item.body ?? "");
  const mobileTitle = String(item.mobileTitle ?? title);
  const mobileBody = String(item.mobileBody ?? body);
  const kickerClass = tone === "terminal" ? "text-emerald-300" : "text-[var(--color-primary)]";
  const titleClass = tone === "terminal" ? "text-slate-100" : "text-[var(--color-text)]";
  const bodyClass = tone === "terminal" ? "text-slate-300" : "text-[var(--color-secondary)]";
  return (
    <>
      {item.kicker && (
        <p className={`text-xs font-semibold uppercase tracking-wide ${kickerClass}`}>{item.kicker}</p>
      )}
      {title && (
        <>
          <h3 className={`hidden font-semibold md:block ${titleClass}`}>{title}</h3>
          <h3 className={`font-semibold md:hidden ${titleClass} ${wrapClass(item.wrap)}`}>{mobileTitle}</h3>
        </>
      )}
      {body && (
        <>
          <p className={`mt-2 hidden text-sm md:block ${bodyClass}`}>{body}</p>
          <p className={`mt-2 text-sm md:hidden ${bodyClass} ${wrapClass(item.wrap)}`}>{mobileBody}</p>
        </>
      )}
    </>
  );
}

function CommandPalette({ items }: { items: ShowcaseItem[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] shadow-lg">
      <div className="border-b border-[var(--color-border)] bg-[var(--color-surface-muted)] px-4 py-3 text-sm text-[var(--color-secondary)]">
        Search a workflow, client, or report
      </div>
      <ul className="divide-y divide-[var(--color-border)]">
        {items.map((item, index) => (
          <li key={index} className={`flex items-start gap-3 px-4 py-3 ${wrapClass(item.wrap)}`}>
            <span className="mt-1 font-mono text-xs text-[var(--color-secondary)]">{index + 1}</span>
            <div className="min-w-0 flex-1">
              <ItemCopy item={item} />
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Bento({ items, surface }: { items: ShowcaseItem[]; surface?: unknown }) {
  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
      {items.map((item, index) => (
        <article
          key={index}
          className={cn(
            "rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-5 shadow-sm",
            index === 0 ? "md:col-span-4 md:row-span-2" : "md:col-span-2",
            wrapClass(item.wrap),
            cardSurfaceClass(surface),
          )}
        >
          <ItemCopy item={item} />
        </article>
      ))}
    </div>
  );
}

function Terminal({ items }: { items: ShowcaseItem[] }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 text-slate-100 shadow-xl">
      <div className="flex items-center gap-2 border-b border-slate-800 px-4 py-2">
        <span className="h-2.5 w-2.5 rounded-full bg-red-400" aria-hidden />
        <span className="h-2.5 w-2.5 rounded-full bg-amber-400" aria-hidden />
        <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" aria-hidden />
        <span className="ml-2 font-mono text-xs text-slate-400">workflow.sh</span>
      </div>
      <div className="space-y-4 p-4 font-mono text-sm">
        {items.map((item, index) => (
          <div key={index} className={wrapClass(item.wrap)}>
            <p className="text-emerald-300">$ {item.kicker || item.title || "run"}</p>
            <div className="mt-1">
              <ItemCopy
                tone="terminal"
                item={{
                  ...item,
                  kicker: undefined,
                }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ProductShowcaseBlock({ content }: { content: Record<string, unknown> }) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const intro = String(resolved.intro ?? "");
        const variant = String(resolved.variant ?? "bento");
        const items = (Array.isArray(resolved.items) ? resolved.items : []) as ShowcaseItem[];
        if (!heading && items.length === 0) return null;
        return (
          <section className="mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*3)]">
            {(heading || intro) && (
              <div className="mb-6 max-w-3xl">
                {heading && (
                  <h2
                    className="font-bold text-[var(--color-text)]"
                    style={{ fontFamily: "var(--font-heading)", fontSize: "var(--text-h2)" }}
                  >
                    {heading}
                  </h2>
                )}
                {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
              </div>
            )}
            {variant === "command_palette" ? (
              <CommandPalette items={items} />
            ) : variant === "terminal" ? (
              <Terminal items={items} />
            ) : (
              <Bento items={items} surface={resolved.cardSurface} />
            )}
          </section>
        );
      }}
    </AudienceSection>
  );
}

export function ClientExamplesBlock({ content }: { content: Record<string, unknown> }) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const intro = String(resolved.intro ?? "");
        const items = (Array.isArray(resolved.items) ? resolved.items : []) as Array<{
          name?: string;
          industry?: string;
          summary?: string;
          result?: string;
          url?: string;
        }>;
        if (!heading && items.length === 0) return null;
        return (
          <section className="mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*3)]">
            {(heading || intro) && (
              <div className="mb-6 max-w-3xl">
                {heading && <h2 className="text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
                {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {items.map((item, index) => {
                const inner = (
                  <article className={proofCardClass(resolved, "h-full shadow-sm")}>
                    {item.industry && (
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-primary)]">
                        {item.industry}
                      </p>
                    )}
                    {item.name && <h3 className={`mt-1 text-lg font-semibold ${cardHeadingClass(resolved.cardSurface)}`}>{item.name}</h3>}
                    {item.summary && <p className={`mt-2 text-sm ${cardBodyClass(resolved.cardSurface)}`}>{item.summary}</p>}
                    {item.result && (
                      <p className="mt-3 text-sm font-medium text-[var(--color-text)]">Result: {item.result}</p>
                    )}
                  </article>
                );
                return item.url ? (
                  <Link key={index} href={item.url} className="block h-full hover:opacity-90">
                    {inner}
                  </Link>
                ) : (
                  <div key={index}>{inner}</div>
                );
              })}
            </div>
          </section>
        );
      }}
    </AudienceSection>
  );
}

export function BeforeAfterBlock({ content }: { content: Record<string, unknown> }) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const intro = String(resolved.intro ?? "");
        const items = (Array.isArray(resolved.items) ? resolved.items : []) as Array<{
          title?: string;
          before?: string;
          after?: string;
          beforeLabel?: string;
          afterLabel?: string;
        }>;
        if (!heading && items.length === 0) return null;
        return (
          <section className="mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*3)]">
            {(heading || intro) && (
              <div className="mb-6 max-w-3xl">
                {heading && <h2 className="text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
                {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2">
              {items.map((item, index) => (
                <article
                  key={index}
                  className={proofCardClass(resolved)}
                >
                  {item.title && <h3 className="mb-4 text-lg font-semibold text-[var(--color-text)]">{item.title}</h3>}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-lg bg-[var(--color-surface-muted)] p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-secondary)]">
                        {item.beforeLabel || "Before"}
                      </p>
                      <p className="mt-2 text-sm text-[var(--color-text)]">{item.before}</p>
                    </div>
                    <div className="rounded-lg border border-[var(--color-primary)]/20 bg-[var(--color-primary)]/5 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-primary)]">
                        {item.afterLabel || "After"}
                      </p>
                      <p className="mt-2 text-sm text-[var(--color-text)]">{item.after}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        );
      }}
    </AudienceSection>
  );
}

export function ProblemSolutionResultBlock({ content }: { content: Record<string, unknown> }) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const intro = String(resolved.intro ?? "");
        const items = (Array.isArray(resolved.items) ? resolved.items : []) as Array<{
          industry?: string;
          problem?: string;
          solution?: string;
          result?: string;
        }>;
        if (!heading && items.length === 0) return null;
        return (
          <section className="mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*3)]">
            {(heading || intro) && (
              <div className="mb-6 max-w-3xl">
                {heading && <h2 className="text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
                {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
              </div>
            )}
            <div className="flex flex-col gap-4">
              {items.map((item, index) => (
                <article
                  key={index}
                  className={proofCardClass(resolved)}
                >
                  {item.industry && (
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-[var(--color-primary)]">
                      {item.industry}
                    </p>
                  )}
                  <div className="grid gap-4 md:grid-cols-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-secondary)]">Problem</p>
                      <p className="mt-2 text-sm text-[var(--color-text)]">{item.problem}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-secondary)]">Solution</p>
                      <p className="mt-2 text-sm text-[var(--color-text)]">{item.solution}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-primary)]">Result</p>
                      <p className="mt-2 text-sm font-medium text-[var(--color-text)]">{item.result}</p>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        );
      }}
    </AudienceSection>
  );
}

export function UseCasesBlock({ content }: { content: Record<string, unknown> }) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const intro = String(resolved.intro ?? "");
        const items = (Array.isArray(resolved.items) ? resolved.items : []) as Array<{
          industry?: string;
          title?: string;
          body?: string;
        }>;
        if (!heading && items.length === 0) return null;
        return (
          <section className="mx-auto max-w-6xl px-4 py-[calc(var(--space-unit)*3)]">
            {(heading || intro) && (
              <div className="mb-6 max-w-3xl">
                {heading && <h2 className="text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
                {intro && <p className="mt-2 text-[var(--color-secondary)]">{intro}</p>}
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {items.map((item, index) => (
                <article
                  key={index}
                  className={proofCardClass(resolved)}
                >
                  {item.industry && (
                    <p className="text-xs font-semibold uppercase tracking-wide text-[var(--color-primary)]">
                      {item.industry}
                    </p>
                  )}
                  {item.title && <h3 className="mt-1 text-lg font-semibold text-[var(--color-text)]">{item.title}</h3>}
                  {item.body && <p className="mt-2 text-sm text-[var(--color-secondary)]">{item.body}</p>}
                </article>
              ))}
            </div>
          </section>
        );
      }}
    </AudienceSection>
  );
}

export function CtaOfferBlock({ content }: { content: Record<string, unknown> }) {
  return (
    <AudienceSection content={content}>
      {(resolved) => {
        const heading = String(resolved.heading ?? "");
        const body = String(resolved.body ?? "");
        const offers = (Array.isArray(resolved.offers) ? resolved.offers : []) as Array<{
          text?: string;
          helper?: string;
          url?: string;
        }>;
        if (!heading && offers.length === 0) return null;
        return (
          <section className="bg-[var(--color-surface-muted)] py-[calc(var(--space-unit)*3)]">
            <div className="mx-auto max-w-6xl px-4">
              <div className="max-w-3xl">
                {heading && <h2 className="text-3xl font-bold text-[var(--color-text)]">{heading}</h2>}
                {body && <p className="mt-2 text-lg text-[var(--color-secondary)]">{body}</p>}
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {offers
                  .filter((offer) => offer.text && offer.url)
                  .map((offer, index) => (
                    <Link
                      key={index}
                      href={String(offer.url)}
                      className={proofCardClass(resolved, "shadow-sm hover:border-[var(--color-primary)]")}
                    >
                      <p className="text-lg font-semibold text-[var(--color-text)]">{offer.text}</p>
                      {offer.helper && <p className="mt-2 text-sm text-[var(--color-secondary)]">{offer.helper}</p>}
                    </Link>
                  ))}
              </div>
            </div>
          </section>
        );
      }}
    </AudienceSection>
  );
}
